/**
 * Epic 9 — Doctrine Requirements Engine.
 *
 * Turns hard-coded thresholds (the "8 BA operators", 200 m hydrant distance…)
 * into governed DoctrineRequirements with full provenance, applicability
 * scoping (role / level / locality / incident-type), conflict detection and an
 * SME-approval lifecycle. The deterministic resolver picks the most specific
 * applicable requirement for a given operational context; the result feeds
 * resolveConfig() in config.ts and baOperatorRequirement() in doctrine.ts.
 *
 * Resolver contract (in priority order):
 *   1. Specificity — more constrained applicability beats less constrained.
 *   2. Recency     — among equals, more recently approved wins.
 *   3. Conservative tiebreak — min→highest value; max→lowest value; eq→conflict.
 *
 * Only SME-approved requirements are resolved. Extracted / sme-review entries
 * are visible in the admin UI but never reach the rule engine.
 */

import type { IncidentStage } from './stages.js';

// ── Governance lifecycle ──────────────────────────────────────────────────────

/**
 * extracted  — AI has proposed a requirement from a doc; not yet reviewed.
 * sme-review — sent to a subject-matter expert; editable; not live.
 * approved   — SME-signed-off; used by the resolver.
 * superseded — replaced by a newer requirement; excluded from resolution.
 */
export type GovernanceStatus = 'extracted' | 'sme-review' | 'approved' | 'superseded';

// ── Parameter keys ────────────────────────────────────────────────────────────

/**
 * Every key maps to a DutyConfig field (see config.ts resolveConfig) — except
 * evBatterySuppressionMinL, which deliberately has no CONFIG_PARAM_MAP entry:
 * public sources disagree on a single water-volume figure (see the two
 * data/requirements/ev-battery-suppression-water-*.json entries), so it exists
 * only to exercise resolveRequirements()'s conflict-reporting path, never to
 * gate a live threshold in a rule.
 */
export type RequirementParameterKey =
  | 'minBaOperators'
  | 'hydrantConcernDistanceM'
  | 'sustainedFlowLpm'
  | 'bulkWaterMinL'
  | 'limitedOnboardL'
  | 'evStorageExclusionDistanceM'
  | 'evBatterySuppressionMinL';

export interface RequirementParameter {
  key: RequirementParameterKey;
  /** min = at least this value; max = no more than this; eq = exactly this. */
  operator: 'min' | 'max' | 'eq';
  value: number;
  unit?: string;
}

// ── Outcomes ──────────────────────────────────────────────────────────────────

export type OutcomeType = 'check' | 'action' | 'record' | 'escalate';

export interface RequirementOutcome {
  type: OutcomeType;
  description: string;
  /** If set, this outcome only applies to the listed roles. */
  roles?: string[];
}

// ── Applicability ─────────────────────────────────────────────────────────────

export interface RequirementApplicability {
  states?: string[];
  agencies?: string[];
  incidentTypes?: string[];
  /** Response level — e.g. 'Level1' | 'Level2' | 'Level3'. */
  levels?: string[];
  roles?: string[];
  localities?: string[];
  stages?: IncidentStage[];
}

// ── Core requirement ──────────────────────────────────────────────────────────

export interface DoctrineRequirement {
  id: string;
  parameter: RequirementParameter;
  sourceDocId: string;
  sourceTitle: string;
  sourceVersion?: string;
  /** ISO-8601 date string — when this was first extracted. */
  extractedAt: string;
  /** ISO-8601 date string — when an SME approved it. */
  approvedAt?: string;
  status: GovernanceStatus;
  /** True for all POC stand-in requirements; replace with authoritative sources. */
  standIn: boolean;
  applicability: RequirementApplicability;
  outcomes: RequirementOutcome[];
  notes?: string;
}

// ── Resolution query ──────────────────────────────────────────────────────────

export interface RequirementQuery {
  state?: string;
  /** All agencies involved (lead + supporting). */
  agencies?: string[];
  /**
   * Every incident type currently believed active (primary + any co-active
   * additional types). A requirement matches if it's untyped (applies to all
   * types — e.g. type-agnostic water-supply doctrine) or shares at least one
   * type with this list.
   */
  incidentTypes?: string[];
  level?: string;
  role?: string;
  locality?: string;
  stage?: IncidentStage;
}

// ── Resolution results ────────────────────────────────────────────────────────

export interface ResolvedParam {
  key: RequirementParameterKey;
  value: number;
  operator: 'min' | 'max' | 'eq';
  unit?: string;
  requirementId: string;
  sourceTitle: string;
  standIn: boolean;
  specificity: number;
}

export interface RequirementConflict {
  key: RequirementParameterKey;
  conflictingIds: string[];
  values: number[];
  tiebreakerApplied: boolean;
  resolved?: number;
}

export interface ResolvedRequirements {
  params: Map<RequirementParameterKey, ResolvedParam>;
  conflicts: RequirementConflict[];
}

// ── Applicability matching ────────────────────────────────────────────────────

const dimMatches = (allowed: string[] | undefined, value: string | undefined): boolean => {
  if (!allowed || allowed.length === 0) return true;
  if (!value) return false;
  return allowed.map((a) => a.toLowerCase()).includes(value.toLowerCase());
};

const anyMatches = (allowed: string[] | undefined, values: string[] | undefined): boolean => {
  if (!allowed || allowed.length === 0) return true;
  if (!values || values.length === 0) return false;
  const set = new Set(allowed.map((a) => a.toLowerCase()));
  return values.some((v) => set.has(v.toLowerCase()));
};

/** True if the requirement applies to this operational context. */
export function requirementApplicabilityMatches(
  req: DoctrineRequirement,
  q: RequirementQuery,
): boolean {
  const a = req.applicability;
  return (
    dimMatches(a.states, q.state) &&
    anyMatches(a.agencies, q.agencies) &&
    anyMatches(a.incidentTypes, q.incidentTypes) &&
    dimMatches(a.levels, q.level) &&
    dimMatches(a.roles, q.role) &&
    dimMatches(a.localities, q.locality) &&
    (!a.stages || a.stages.length === 0 || (q.stage != null && a.stages.includes(q.stage)))
  );
}

/**
 * Specificity score — the number of applicability dimensions that are
 * constrained (non-empty) in the requirement. A universal requirement (no
 * constraints) scores 0; a fully-scoped one scores up to 7.
 */
function specificityScore(req: DoctrineRequirement): number {
  const a = req.applicability;
  return [
    a.states?.length,
    a.agencies?.length,
    a.incidentTypes?.length,
    a.levels?.length,
    a.roles?.length,
    a.localities?.length,
    a.stages?.length,
  ].reduce<number>((sum, len) => sum + (len ? 1 : 0), 0);
}

// ── Resolver ──────────────────────────────────────────────────────────────────

/**
 * Resolve all approved, applicable requirements for a query context.
 *
 * Groups by parameter key, picks the winner per key using specificity →
 * recency → conservative tiebreak. Conflicts are reported (same key, same
 * specificity, different values where conservative tiebreak cannot help).
 */
export function resolveRequirements(
  requirements: DoctrineRequirement[],
  query: RequirementQuery,
): ResolvedRequirements {
  const params = new Map<RequirementParameterKey, ResolvedParam>();
  const conflicts: RequirementConflict[] = [];

  // Only approved requirements enter the resolver.
  const eligible = requirements.filter(
    (r) => r.status === 'approved' && requirementApplicabilityMatches(r, query),
  );

  // Group by parameter key.
  const byKey = new Map<RequirementParameterKey, typeof eligible>();
  for (const req of eligible) {
    const key = req.parameter.key;
    const group = byKey.get(key) ?? [];
    group.push(req);
    byKey.set(key, group);
  }

  for (const [key, group] of byKey) {
    // Sort by specificity desc, then recency desc (approvedAt or extractedAt).
    const scored = group.map((r) => ({ r, score: specificityScore(r) }));
    const maxScore = Math.max(...scored.map((s) => s.score));
    const candidates = scored.filter((s) => s.score === maxScore);

    if (candidates.length === 1) {
      const { r } = candidates[0]!;
      params.set(key, {
        key,
        value: r.parameter.value,
        operator: r.parameter.operator,
        unit: r.parameter.unit,
        requirementId: r.id,
        sourceTitle: r.sourceTitle,
        standIn: r.standIn,
        specificity: maxScore,
      });
      continue;
    }

    // Multiple candidates at the same specificity — attempt conservative tiebreak.
    const op = candidates[0]!.r.parameter.operator;
    const values = candidates.map((c) => c.r.parameter.value);
    const allSameOp = candidates.every((c) => c.r.parameter.operator === op);

    let resolved: number | undefined;
    let tiebreakerApplied = false;

    if (allSameOp && op === 'min') {
      resolved = Math.max(...values); // most demanding minimum
      tiebreakerApplied = true;
    } else if (allSameOp && op === 'max') {
      resolved = Math.min(...values); // most restrictive maximum
      tiebreakerApplied = true;
    }

    const unique = new Set(values);
    if (unique.size > 1 || !allSameOp) {
      conflicts.push({
        key,
        conflictingIds: candidates.map((c) => c.r.id),
        values,
        tiebreakerApplied,
        resolved,
      });
    }

    if (resolved !== undefined) {
      // Pick the candidate whose value matches the tiebreaker result.
      const winner = candidates.find((c) => c.r.parameter.value === resolved)!.r;
      params.set(key, {
        key,
        value: resolved,
        operator: op,
        unit: winner.parameter.unit,
        requirementId: winner.id,
        sourceTitle: winner.sourceTitle,
        standIn: winner.standIn,
        specificity: maxScore,
      });
    }
    // If unresolvable conflict (eq operator, different values), no param is set
    // and the caller falls back to DEFAULT_CONFIG.
  }

  return { params, conflicts };
}
